import { Logo } from '../../Logo';
import { Box, Stack, Typography } from '@mui/material';
import { XpmLogo } from '../XpmLogo';

type XpmConnectionIllustrationVariant = 'connected' | 'disconnected';

type XpmConnectionIllustrationProps = {
  variant: XpmConnectionIllustrationVariant;
};

/**
 * Shared illustration component showing VerifiMe to XPM connection status.
 * Used in both XpmIntegrationSuccessModal and XpmDisconnectConfirmModal.
 *
 * Figma references:
 * - XPM Success Modal: node-id=1865-17999 (larger, with background containers)
 * - XPM Disconnect Modal: node-id=1869-18412 (larger, with background containers)
 *
 * ## Hardcoded values from Figma
 *
 * Figma specs this modal illustration with its own sizes (100px logo containers on light surface
 * boxes, a 3px connector), which no logo token expresses, so it uses these values.
 *
 * - Container width: 300px
 * - Logo container size: 100x100px
 * - Connector line thickness: 3px
 * - Logo container border: 1px
 */
export const XpmConnectionIllustration = ({
  variant,
}: Readonly<XpmConnectionIllustrationProps>) => {
  const LogoContainer = ({ children }: { children: React.ReactNode }) => (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={(theme) => ({
        width: '100px',
        height: '100px',
        backgroundColor: theme.palette.surface.logo,
        borderRadius: theme.semantic.borderRadius.sm,
        padding: theme.semantic.spacing['2xs'],
        border: `1px solid ${theme.palette.grey[100]}`,
      })}
    >
      {children}
    </Stack>
  );

  const ConnectorLine = () => (
    <Box
      sx={(theme) => ({
        flex: 1,
        height: 0,
        borderTop: `3px dashed ${theme.palette.border.decorative}`,
      })}
    />
  );

  return (
    <Stack
      direction={['column', 'row']}
      alignItems="center"
      justifyContent="center"
      sx={{ width: '300px' }}
    >
      {/* Verifime Logo */}
      <LogoContainer>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& svg': {
              width: '100%',
              height: '100%',
            },
          }}
        >
          <Logo layout="markOnly" />
        </Box>
      </LogoContainer>

      {/* Connector */}
      {variant === 'connected' ? (
        <ConnectorLine />
      ) : (
        <Stack direction="row" alignItems="center" sx={{ flex: 1 }}>
          <ConnectorLine />
          <Typography variant="h4" color="error.main">
            X
          </Typography>
          <ConnectorLine />
        </Stack>
      )}

      {/* XPM Logo */}
      <LogoContainer>
        <XpmLogo size="medium" />
      </LogoContainer>
    </Stack>
  );
};

export type { XpmConnectionIllustrationVariant, XpmConnectionIllustrationProps };
